/**
 * Product list for the question before sign-up (#2630).
 *
 * Mirrors `backend/src/components/products.py`. A backend regression test
 * (`test_issue_2630_product_preset.py`) keeps the keys, names and kinds in
 * step. Public names are placeholders the founder will replace.
 *
 * Whether a product is offered is NOT here: it comes from the API
 * (`/api/products` when signed out, `products_offered` on the account).
 */

import { apiFetch } from "../api/client";
import { CONTACT_EMAIL } from "../routes/marketing/MarketingChrome";

export type ProductKey = "shifts" | "rooms" | "bookings" | "crews" | "tasks" | "events";

export interface ProductDef {
  key: ProductKey;
  name: string;
  teamKind: "scheduling" | "tasks";
  /** False: no answer card. Reached through `/start?product=<key>` only. */
  listed: boolean;
  /** The answer card text. */
  answer: string;
  /** One example line under the answer. */
  example: string;
  /** Three plain lines on the confirmation screen. */
  startsWith: readonly [string, string, string];
}

export const PRODUCTS: readonly ProductDef[] = [
  {
    key: "shifts",
    name: "Shift rosters",
    teamKind: "scheduling",
    listed: true,
    answer: "The same posts every day",
    example: "Two guards on the front desk on every shift.",
    startsWith: [
      "Your team starts on a roster of posts and shifts.",
      "You name the posts and the roles, then invite your people.",
      "You can switch parts on or off at any time.",
    ],
  },
  {
    key: "rooms",
    name: "Rosters by room",
    teamKind: "scheduling",
    listed: true,
    answer: "It depends on which rooms open",
    example: "Six clinic rooms on Monday, four on Tuesday.",
    startsWith: [
      "Your team starts on a roster built around rooms.",
      "You say which rooms open on which days.",
      "You can switch parts on or off at any time.",
    ],
  },
  {
    key: "bookings",
    name: "Rosters from bookings",
    teamKind: "scheduling",
    listed: true,
    answer: "It depends on bookings",
    example: "More covers booked on Friday, more staff on Friday.",
    startsWith: [
      "Your team starts on a roster that follows demand.",
      "Busy days ask for more people.",
      "You can switch parts on or off at any time.",
    ],
  },
  {
    key: "crews",
    name: "Crew rosters",
    teamKind: "scheduling",
    listed: true,
    answer: "It depends on flights or jobs",
    example: "Each flight needs a crew of five.",
    startsWith: [
      "Your team starts on a roster of crews for flights or jobs.",
      "Your crew gets notices in Telegram.",
      "You can switch parts on or off at any time.",
    ],
  },
  {
    key: "tasks",
    name: "Team tasks",
    teamKind: "tasks",
    listed: true,
    answer: "We hand out tasks",
    example: "Open the doors, check tickets, run the bar.",
    startsWith: [
      "Your team starts on a map of tasks.",
      "Each task has an owner, notes and reminders.",
      "You can switch parts on or off at any time.",
    ],
  },
  {
    key: "events",
    name: "Event days",
    teamKind: "tasks",
    listed: false,
    answer: "We run events",
    example: "Doors open at six, and a crew of volunteers works from one run sheet.",
    startsWith: [
      "Your team starts on a map of tasks for one event day.",
      "Each person sees their own branch of the work.",
      "You can switch parts on or off at any time.",
    ],
  },
];

export function productByKey(key: string | null | undefined): ProductDef | null {
  return PRODUCTS.find((p) => p.key === key) ?? null;
}

export const PRODUCT_TRIAL_EMAIL = CONTACT_EMAIL;
export const PRODUCT_BUILDING_PLAIN = "We are building this.";
export const PRODUCT_NOTIFY_BUTTON = "Tell me when this opens";
export const PRODUCT_NOTIFY_THANKS = "Thanks. We will write to you when this opens.";
export const PRODUCT_NOTED = "Thanks. We noted it.";
export const PRODUCT_EMAIL_NUDGE =
  "Add an email and we will write to you when this opens. Or press again to note it without one.";
export const PRODUCT_EMAIL_BAD = "That email does not look right.";
export const PRODUCT_SAVE_FAILED = "Couldn't save that. Try again.";

/** Loose check: text, an @, then a dot with text after it. */
export function emailLooksRight(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const ANON_KEY = "tarii.anon_id";

function anonId(): string | undefined {
  try {
    let id = window.sessionStorage.getItem(ANON_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      window.sessionStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return undefined;
  }
}

/**
 * Record a pick on the product question (#2651). Returns the request: a
 * pick click ignores it (`void` plus `.catch`), the closed card awaits it. `key` is a product key or "other".
 */
export function postProductAnswer(key: string, extra: { note?: string; email?: string } = {}) {
  const body: Record<string, string> = { product_key: key };
  const anon = anonId();
  if (anon) body.anon_id = anon;
  if (extra.note?.trim()) body.note = extra.note.trim();
  if (extra.email?.trim()) body.email = extra.email.trim();
  return apiFetch("/api/products/answers", { method: "POST", body: JSON.stringify(body) });
}

/**
 * The sixth answer: "Something else", with one free-text line. It
 * confirms as Shift rosters. The text is kept in the stash (see
 * `lib/signupProduct`), not sent to the API.
 */
export const SOMETHING_ELSE = {
  answer: "Something else",
  example: "Tell us in one line. We start you on Shift rosters.",
  startsKey: "shifts" as ProductKey,
} as const;
